/**
 * Async generator that yields parsed JSON events from a Server-Sent Events
 * response body. Tolerant of partial chunks split across `data:` lines.
 *
 * Usage:
 *   for await (const evt of readSSE(response)) {
 *     // evt is the JSON-decoded `data:` payload
 *   }
 *
 * The default skips `event:` lines and only yields each `data:` line's
 * JSON payload. Pass { withEvent: true } to receive `{ id, event, data }`
 * tuples so consumers can dispatch by SSE event name.
 *
 * `id` follows SSE's last-event-id semantics: it persists until the stream sets
 * a new one, so a heartbeat comment between two events doesn't clear it. It is
 * what makes a dropped stream resumable — the builder chat sends the last id it
 * received back as `from` and gets the rest replayed (see useBuilderChatStream).
 *
 * Comment frames (`: ping`) are ignored, as the spec requires. They exist so the
 * connection is never idle long enough for a proxy to cut it — and, with
 * `idleMs`, so the CLIENT can tell a live stream from a dead one.
 *
 * `idleMs` is the silence the caller is willing to accept before declaring the
 * stream dead and ending the iteration. It exists because the failure that loses
 * an answer is not the connection that errors — it is the one that goes quiet
 * without ever erroring (a laptop asleep, a wifi handover, a NAT entry expiring):
 * `reader.read()` then waits forever, no exception is thrown, and the caller has
 * nothing to react to. A server that heartbeats makes that silence meaningful,
 * and this turns it into an ordinary end-of-stream the caller already handles.
 */
export async function* readSSE(response, { withEvent = false, idleMs = 0 } = {}) {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent = null
  let lastId = null
  try {
    while (true) {
      let chunk
      if (idleMs > 0) {
        // Race the read against the silence budget. A heartbeat comment resolves
        // the read like any other chunk, so any live stream keeps winning.
        let timer
        const idle = new Promise((resolve) => { timer = setTimeout(() => resolve(null), idleMs) })
        chunk = await Promise.race([reader.read(), idle])
        clearTimeout(timer)
        if (chunk === null) {
          // Cancel so the still-pending read releases the socket instead of
          // leaking it, then end the iteration like a normal close.
          await reader.cancel().catch(() => {})
          break
        }
      }
      else {
        chunk = await reader.read()
      }
      const { done, value } = chunk
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) {
          // Blank line = end of one SSE event, reset event name.
          currentEvent = null
          continue
        }
        if (trimmed.startsWith('id:')) {
          lastId = trimmed.slice(3).trim()
          continue
        }
        if (trimmed.startsWith('event:')) {
          currentEvent = trimmed.slice(6).trim()
          continue
        }
        if (!trimmed.startsWith('data:')) continue
        const payload = trimmed.slice(5).trim()
        if (!payload) continue
        try {
          const data = JSON.parse(payload)
          if (withEvent) yield { id: lastId, event: currentEvent, data }
          else yield data
        } catch { /* skip unparsable */ }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
